/**
 * Discount Apply Component
 * Handles discount code application and removal in cart drawer
 */

class DiscountApply {
  constructor() {
    this.form = document.getElementById('discount-apply-form');
    this.input = document.getElementById('discount-code-input');
    this.button = document.getElementById('discount-apply-button');
    this.buttonText = document.getElementById('discount-button-text');
    this.loading = document.getElementById('discount-loading');
    this.errorElement = document.getElementById('discount-error');
    this.successElement = document.getElementById('discount-success');
    this.tagsContainer = document.getElementById('discount-tags');

    // Abort controller for managing requests
    this.activeFetch = null;

    // Initialize if we have the minimum required elements
    if (this.input && this.button) {
      this.init();
    }
  }

  init() {
    // Bind button click
    if (this.button) {
      this.button.addEventListener('click', (e) => {
        if (e.defaultPrevented) return;
        e.preventDefault();
        this.handleSubmit(e);
      });
    }
    
    // Bind Enter key to input field
    if (this.input) {
      this.input.addEventListener('keydown', (e) => {
        if (e.defaultPrevented || e.isComposing || e.keyCode === 229 || e.repeat) return;
        if (e.key === 'Enter') {
          e.preventDefault();
          this.handleSubmit(e);
        }
      });
    }
  }

  /**
   * Reinitialize the component after DOM updates
   */
  reinitialize() {
    this.form = document.getElementById('discount-apply-form');
    this.input = document.getElementById('discount-code-input');
    this.button = document.getElementById('discount-apply-button');
    this.buttonText = document.getElementById('discount-button-text');
    this.loading = document.getElementById('discount-loading');
    this.errorElement = document.getElementById('discount-error');
    this.successElement = document.getElementById('discount-success');
    this.tagsContainer = document.getElementById('discount-tags');

    this.init();
  }

  /**
   * Create abort controller for request management
   */
  createAbortController() {
    if (this.activeFetch) {
      this.activeFetch.abort();
    }
    const abortController = new AbortController();
    this.activeFetch = abortController;
    return abortController;
  }

  /**
   * Get currently applied discount codes from DOM
   */
  getExistingDiscounts() {
    const discountCodes = [];
    const discountTags = document.querySelectorAll('.discount-apply__tag[data-discount-code]');
    
    discountTags.forEach(tag => {
      const code = tag.getAttribute('data-discount-code');
      if (code) {
        discountCodes.push(code);
      }
    });

    return discountCodes;
  }

  /**
   * Apply discount code using fetch API
   */
  async handleSubmit(event) {
    if (this.activeFetch) return;
    // Resolve fresh DOM references from the event target to avoid stale nodes after re-renders
    const container = (event && event.target && event.target.closest && event.target.closest('#discount-apply-form'))
      || document.getElementById('discount-apply-form');

    const inputEl = (container && container.querySelector('#discount-code-input'))
      || document.getElementById('discount-code-input');
    const buttonEl = (container && container.querySelector('#discount-apply-button'))
      || document.getElementById('discount-apply-button');
    const loadingEl = (container && container.querySelector('#discount-loading'))
      || document.getElementById('discount-loading');
    const buttonTextEl = (container && container.querySelector('#discount-button-text'))
      || document.getElementById('discount-button-text');
    const errorEl = (container && container.querySelector('#discount-error'))
      || document.getElementById('discount-error');
    const successEl = (container && container.querySelector('#discount-success'))
      || document.getElementById('discount-success');

    // Update instance refs to current DOM so UI helpers operate on live nodes
    if (inputEl) this.input = inputEl;
    if (buttonEl) this.button = buttonEl;
    if (loadingEl) this.loading = loadingEl;
    if (buttonTextEl) this.buttonText = buttonTextEl;
    if (errorEl) this.errorElement = errorEl;
    if (successEl) this.successElement = successEl;

    if (!inputEl) {
      return;
    }

    const discountCode = (inputEl.value || '').trim().toUpperCase();
    
    if (!discountCode) {
      return;
    }

    const existingDiscounts = this.getExistingDiscounts();
    
    if (existingDiscounts.includes(discountCode)) {
      this.showError('Discount code already applied');
      this.input.value = '';
      return;
    }

    this.showLoading(true);
    this.hideMessages();

    const abortController = this.createAbortController();

    try {
      // Prepare the discount codes array
      const allDiscounts = [...existingDiscounts, discountCode];
      
      // Prepare request body
      const body = JSON.stringify({
        discount: allDiscounts.join(','),
        sections: ['cart-drawer']
      });

      // Make fetch request to update cart with discount
      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body,
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Check if discount was successfully applied by examining the response
      let isValid = true;
      let errorMessage = 'Ungültiger Rabattcode';

      if (data.discount_codes) {
        const targetDiscount = data.discount_codes.find(discount => 
          discount.code === discountCode
        );

        if (targetDiscount && targetDiscount.applicable === false) {
          isValid = false;
        }
      } else if (data.errors) {
        isValid = false;
        errorMessage = 'Unable to apply discount code';
      }

      if (!isValid) {
        this.showError(errorMessage);
        this.input.value = '';
        return;
      }
      
      // Update the cart drawer UI
      await this.updateCartDrawer();
      
      // Clear input and show success
      this.input.value = '';
      this.showSuccess('Rabattcode erfolgreich angewendet');

    } catch (error) {
      if (error.name !== 'AbortError') {
        this.showError('Failed to apply discount code');
      }
    } finally {
      this.showLoading(false);
      this.activeFetch = null;
    }
  }

  /**
   * Update cart drawer DOM after discount changes
   */
  async updateCartDrawer() {
    try {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      const responseText = await response.text();
      
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const selectors = ['cart-drawer-items', '.ug-drawer__footer', '.drawer__footer'];
      
      for (const selector of selectors) {
        const targetElement = document.querySelector(selector);
        const sourceElement = html.querySelector(selector);
        if (targetElement && sourceElement) {
          if (selector === '.ug-drawer__footer') {
            ['.ug-drawer__discount-details', '.ug-drawer__details-panel'].forEach((detailsSelector) => {
              const previous = targetElement.querySelector(detailsSelector);
              const next = sourceElement.querySelector(detailsSelector);
              if (previous && next) next.open = previous.open;
            });
          }
          targetElement.replaceWith(sourceElement);
        }
      }

      // Bind to the replaced footer before callers update loading and messages.
      if (window.discountApplyInstance) {
        window.discountApplyInstance.reinitialize();
      } else {
        window.discountApplyInstance = new DiscountApply();
      }
      cleanupDuplicateRemoveDiscountPopups();
      setTimeout(()=>{
        (async ()=> {
          if(!window.Glood) return
          await Glood.upsell.refresh({ refetchCart: true })
          await Glood.upsell.miniCart.hydrate({ 
            freeGiftSurface: 'cart',
            drawer: {
              selector: 'cart-drawer',   
              openClass: 'active',       
            },
          })
        })()
      })
    } catch (error) {
      // Handle error silently
    }
  }

  showLoading(show) {
    this.button.disabled = show;
    this.button.classList.toggle('is-loading', show);
    this.button.setAttribute('aria-busy', String(show));
    this.loading.classList.toggle('show', show);
    this.buttonText.style.opacity = show ? '0' : '1';
  }

  showError(message) {
    const messageElement = this.errorElement.querySelector('[data-discount-error-message]') || this.errorElement;
    messageElement.textContent = message;
    this.errorElement.classList.add('show');
    this.successElement.classList.remove('show');
  }

  showSuccess(message) {
    this.successElement.textContent = message;
    this.successElement.classList.add('show');
    this.errorElement.classList.remove('show');
  }

  hideMessages() {
    this.errorElement.classList.remove('show');
    this.successElement.classList.remove('show');
  }
}

// Global variable to store discount code to be removed
let discountCodeToRemove = null;

/**
 * Show remove discount confirmation popup
 */
function removeDiscount(discountCode) {
  // Store the discount code to be removed
  discountCodeToRemove = discountCode;
  
  // Update the popup with the discount code
  const discountCodeSpan = document.getElementById('discount-code-to-remove');
  if (discountCodeSpan) {
    discountCodeSpan.textContent = discountCode;
  }
  
  // Load UgCommonPopup script if not already loaded
  if (!customElements.get('ug-common-popup')) {
    const script = document.createElement('script');
    script.src = document.querySelector('[src*="ug-popup.js"]')?.src || '/assets/ug-popup.js';
    document.head.appendChild(script);
    
    // Wait for custom element to be defined before proceeding
    customElements.whenDefined('ug-common-popup').then(() => {
      openRemoveDiscountPopup(discountCode);
    });
    return;
  }
  
  openRemoveDiscountPopup(discountCode);
}

/**
 * Clean up duplicate remove discount popups
 */
function cleanupDuplicateRemoveDiscountPopups() {
  const allPopups = document.querySelectorAll('#remove-discount-popup');
  
  // If there's only one or no popup, return the first one (or null)
  if (allPopups.length <= 1) {
    return allPopups[0] || null;
  }
  
  // Keep the one that's already in body, or the first one
  let keepPopup = Array.from(allPopups).find(popup => popup.parentNode === document.body) || allPopups[0];
  
  // Remove all others
  allPopups.forEach(popup => {
    if (popup !== keepPopup) {
      // Ensure popup is closed before removing
      if (popup.isVisible) {
        popup.close();
      }
      popup.remove();
    }
  });
  
  return keepPopup;
}

/**
 * Helper function to open the remove discount popup
 */
function openRemoveDiscountPopup(discountCode) {
  // Clean up any duplicate popups first
  let popup = cleanupDuplicateRemoveDiscountPopups();
  
  // If no popup found, try to get it directly
  if (!popup) {
    popup = document.getElementById('remove-discount-popup');
  }
  
  if (!popup) {
    console.error('Remove discount popup not found');
    return;
  }
  
  // Ensure popup is properly attached to body
  if (popup.parentNode !== document.body) {
    document.body.appendChild(popup);
  }
  
  // Reset popup state in case it's stuck from previous interaction
  popup.removeAttribute('visible');
  popup.isVisible = false;
  
  // Small delay to ensure proper reset before opening
  setTimeout(() => {
    popup.open();
  }, 10);
}

/**
 * Cancel remove discount action
 */
function cancelRemoveDiscount() {
  discountCodeToRemove = null;
  
  // Try to find the active popup
  let popup = document.body.querySelector('#remove-discount-popup');
  if (!popup) {
    popup = document.getElementById('remove-discount-popup');
  }
  
  if (popup) {
    popup.close();
  }
}

/**
 * Confirm remove discount action
 */
async function confirmRemoveDiscount() {
  if (!discountCodeToRemove) return;
  
  // Get popup reference before any DOM manipulation
  let popup = document.body.querySelector('#remove-discount-popup');
  if (!popup) {
    popup = document.getElementById('remove-discount-popup');
  }
  
  // Close the popup immediately for better user experience
  if (popup) {
    popup.close();
  }
  
  try {
    // Get current discount codes and remove the specified one
    const discountTags = document.querySelectorAll('.discount-apply__tag[data-discount-code]');
    const existingDiscounts = [];
    
    discountTags.forEach(tag => {
      const code = tag.getAttribute('data-discount-code');
      if (code && code !== discountCodeToRemove) {
        existingDiscounts.push(code);
      }
    });

    // Prepare request body
    const body = JSON.stringify({
      discount: existingDiscounts.join(','),
      sections: ['cart-drawer']
    });

    // Make fetch request to update cart without the removed discount
    const response = await fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Update cart drawer UI
    const cartResponse = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
    const responseText = await cartResponse.text();
    
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const selectors = ['cart-drawer-items', '.ug-drawer__footer', '.drawer__footer'];
    
    for (const selector of selectors) {
      const targetElement = document.querySelector(selector);
      const sourceElement = html.querySelector(selector);
      if (targetElement && sourceElement) {
        if (selector === '.ug-drawer__footer') {
          ['.ug-drawer__discount-details', '.ug-drawer__details-panel'].forEach((detailsSelector) => {
            const previous = targetElement.querySelector(detailsSelector);
            const next = sourceElement.querySelector(detailsSelector);
            if (previous && next) next.open = previous.open;
          });
        }
        targetElement.replaceWith(sourceElement);
      }
    }

    if (window.discountApplyInstance) {
      window.discountApplyInstance.reinitialize();
    } else {
      window.discountApplyInstance = new DiscountApply();
    }
    cleanupDuplicateRemoveDiscountPopups();

    } catch (error) {
      console.error('Failed to remove discount code:', error);
      // Note: Popup is already closed at the beginning of the function
    } finally {
      // Reset the stored discount code
      discountCodeToRemove = null;
    }

    (async ()=> {
      if(!window.Glood) return
      await Glood.upsell.refresh({ refetchCart: true })
      await Glood.upsell.miniCart.hydrate({ 
        freeGiftSurface: 'cart',
        drawer: {
          selector: 'cart-drawer',   
          openClass: 'active',       
        },
      })
    })()
}

// Initialize discount apply component
document.addEventListener('DOMContentLoaded', function() {
  if (!window.discountApplyInstance) {
    try {
      window.discountApplyInstance = new DiscountApply();
    } catch (error) {
      // Handle error silently
    }
  }

  // Global delegated listeners to survive DOM replacements
  if (!window.__discountApplyDelegationAdded) {
    window.__discountApplyDelegationAdded = true;

    // Click delegation for Apply button
    document.addEventListener(
      'click',
      function (e) {
        const btn = e.target && e.target.closest && e.target.closest('#discount-apply-button');
        if (btn) {
          e.preventDefault();
          try {
            if (window.discountApplyInstance) {
              window.discountApplyInstance.handleSubmit(e);
            } else {
              window.discountApplyInstance = new DiscountApply();
              window.discountApplyInstance.handleSubmit(e);
            }
          } catch (err) {
            // Handle error silently
          }
        }
      },
      true
    );

    // Keydown delegation for Enter key in input
    document.addEventListener(
      'keydown',
      function (e) {
        if (e.isComposing || e.keyCode === 229 || e.repeat) return;
        const input = e.target && e.target.closest && e.target.closest('#discount-code-input');
        if (input && (e.key === 'Enter' || e.keyCode === 13)) {
          e.preventDefault();
          try {
            if (window.discountApplyInstance) {
              window.discountApplyInstance.handleSubmit(e);
            } else {
              window.discountApplyInstance = new DiscountApply();
              window.discountApplyInstance.handleSubmit(e);
            }
          } catch (err) {
            // Handle error silently
          }
        }
      },
      true
    );
  }
});



// Make functions globally available
window.removeDiscount = removeDiscount;
window.cleanupDuplicateRemoveDiscountPopups = cleanupDuplicateRemoveDiscountPopups;
window.openRemoveDiscountPopup = openRemoveDiscountPopup;
window.cancelRemoveDiscount = cancelRemoveDiscount;
window.confirmRemoveDiscount = confirmRemoveDiscount;
